import { useEffect, useRef, useState } from 'react';
import { STORE_INFO } from '@/mocks/content';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const INFO_ROWS = [
  { icon: 'ri-time-line', label: '営業時間', value: STORE_INFO.hours },
  { icon: 'ri-calendar-close-line', label: '定休日', value: STORE_INFO.closed },
  { icon: 'ri-map-pin-line', label: '住所', value: STORE_INFO.address },
  { icon: 'ri-phone-line', label: '電話番号', value: STORE_INFO.tel, href: STORE_INFO.telHref },
  { icon: 'ri-instagram-line', label: 'Instagram', value: '@lounge_hana0915', href: STORE_INFO.instagram, external: true },
];

export default function Access() {
  const { ref, inView } = useInView(0.1);

  return (
    <section id="access" className="relative bg-[#0A0A0F] py-24 md:py-36 overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="text-center mb-16">
          <p
            className="text-[#C9A84C] text-xs tracking-[0.5em] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            ACCESS &amp; INFO
          </p>
          <h2
            className="text-white text-3xl md:text-4xl font-bold tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            基本情報・アクセス
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Map */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-40px)',
            }}
          >
            <div className="relative rounded-lg overflow-hidden border border-[#C9A84C]/20 w-full h-[300px] md:h-[400px]">
              <iframe
                title="Lounge華 地図"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3196.5!2d137.2113!3d36.6953!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5ff7959b5e5e5e5e%3A0x0!2z5a6H5bGx5biC5YCJ5pys6Iiq77yR5LiB55uu77yS4oiS77yS77yQ!5e0!3m2!1sja!2sjp!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.85) contrast(0.9)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p
              className="text-white/30 text-xs mt-3 tracking-widest text-center"
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              ※ 地図は参考表示です。詳細はお電話にてご確認ください。
            </p>
          </div>

          {/* Info */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(40px)',
              transitionDelay: '200ms',
            }}
          >
            <div className="space-y-0">
              {INFO_ROWS.map((row, i) => (
                <div
                  key={i}
                  className="flex items-start gap-5 py-5 border-b border-[#C9A84C]/10 last:border-b-0"
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${row.icon} text-[#C9A84C] text-lg`} />
                  </div>
                  <div className="flex-1">
                    <p
                      className="text-[#C9A84C]/70 text-xs tracking-widest mb-1"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {row.label}
                    </p>
                    {row.href ? (
                      <a
                        href={row.href}
                        target={row.external ? '_blank' : undefined}
                        rel={row.external ? 'noopener noreferrer nofollow' : undefined}
                        className="text-white/80 text-sm tracking-widest hover:text-[#C9A84C] transition-colors duration-300 cursor-pointer"
                        style={{ fontFamily: "'Shippori Mincho', serif" }}
                      >
                        {row.value}
                      </a>
                    ) : (
                      <p
                        className="text-white/80 text-sm tracking-widest"
                        style={{ fontFamily: "'Shippori Mincho', serif" }}
                      >
                        {row.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Note */}
            <div className="mt-8 border border-[#C9A84C]/20 rounded-lg p-5 bg-[#C9A84C]/5">
              <p
                className="text-[#C9A84C] text-xs tracking-widest mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Access Note
              </p>
              <p
                className="text-white/60 text-xs leading-[2]"
                style={{ fontFamily: "'Shippori Mincho', serif" }}
              >
                富山市総曲輪エリア、センタービル3階にございます。<br />
                初めてのお客様はお電話にてご確認の上、お越しください。<br />
                近隣に有料駐車場がございます。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
